/**
 * useAuth.ts
 *
 * Owns auth state for the taskpane: the JWT lives in localStorage
 * ("filer.jwt", via api/client.ts's storage helpers — this hook never
 * touches localStorage directly), login exchanges a dev token for a
 * session JWT via POST /auth/dev-login, and identity comes from a
 * TanStack Query `/me` query gated on a token being present.
 */
import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  api,
  clearStoredToken,
  getStoredToken,
  setStoredToken,
} from "../api/client";
import type { ApiError, DevLoginResponse, MeResponse } from "../api/types";

export interface UseAuthResult {
  isAuthenticated: boolean;
  me: MeResponse | undefined;
  isMeLoading: boolean;
  meError: ApiError | null;
  login: (devToken: string) => void;
  isLoggingIn: boolean;
  loginError: ApiError | null;
  logout: () => void;
}

export function useAuth(): UseAuthResult {
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(() => getStoredToken());

  const meQuery = useQuery<MeResponse, ApiError>({
    queryKey: ["me"],
    queryFn: api.getMe,
    enabled: token !== null,
    retry: false,
  });

  const loginMutation = useMutation<DevLoginResponse, ApiError, string>({
    mutationFn: (devToken) => api.devLogin(devToken),
    onSuccess: (data) => {
      setStoredToken(data.jwt);
      setToken(data.jwt);
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });

  const logout = useCallback(() => {
    clearStoredToken();
    setToken(null);
    queryClient.removeQueries({ queryKey: ["me"] });
  }, [queryClient]);

  return {
    isAuthenticated: token !== null,
    me: meQuery.data,
    isMeLoading: meQuery.isLoading,
    meError: meQuery.error,
    login: (devToken: string) => {
      loginMutation.mutate(devToken);
    },
    isLoggingIn: loginMutation.isPending,
    loginError: loginMutation.error,
    logout,
  };
}
